import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Navbar.css';


const NAV_LINKS = [
  { label: 'Home',     href: '/' },
  { label: 'Services', href: '/services' },
  { label: 'About',    href: '/about' },
  { label: 'Contact',  href: '/contact' },
];

const Navbar = () => {
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [scrolled,  setScrolled]  = useState(false);
  const [langOpen,  setLangOpen]  = useState(false);
  const [lang,      setLang]      = useState('English');
  const langRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handler = (e) => { if (!langRef.current?.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navClass = [
    'navbar',
    isHome && !scrolled ? 'navbar--transparent' : 'navbar--solid',
    scrolled ? 'navbar--shadow' : '',
  ].filter(Boolean).join(' ');

  return (
    <nav className={navClass} role="navigation" aria-label="Main navigation">
      <div className="navbar__inner">

        {/* Logo */}
        <Link to="/" className="navbar__logo" aria-label="VTD Astrology home">
          <SunIcon />
          <div className="navbar__brand-wrap">
            <span className="navbar__brand">VTD</span>
            <span className="navbar__brand-sub">Astrology</span>
          </div>
        </Link>

        {/* Desktop links */}
        <ul className="navbar__links" role="list">
          {NAV_LINKS.map(({ label, href }) => {
            const active = location.pathname === href ||
              (href !== '/' && location.pathname.startsWith(href));
            return (
              <li key={label}>
                <Link
                  to={href}
                  className={`navbar__link${active ? ' navbar__link--active' : ''}`}
                >
                  {label}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Right controls */}
        <div className="navbar__right">
          {/* Language */}
          <div className="lang-drop" ref={langRef}>
            <button className="lang-drop__btn" onClick={() => setLangOpen(o => !o)}
              aria-expanded={langOpen}>
              {lang} <span aria-hidden="true">▾</span>
            </button>
            {langOpen && (
              <ul className="lang-drop__menu" role="listbox">
                {['Telugu', 'Hindi', 'English'].map(l => (
                  <li key={l} role="option" aria-selected={lang === l}
                    className={`lang-drop__item${lang === l ? ' lang-drop__item--active' : ''}`}
                    onClick={() => { setLang(l); setLangOpen(false); }}>
                    {l}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Chat CTA — using <a> because /chat is nginx-served (outside React) */}
          <a href="/chat" className="navbar__wa">
            <WAIcon /> Chat with Tilak
          </a>

          {/* Hamburger */}
          <button
            className={`navbar__burger${menuOpen ? ' navbar__burger--open' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Toggle menu" aria-expanded={menuOpen}>
            <span /><span /><span />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`navbar__mobile${menuOpen ? ' navbar__mobile--open' : ''}`}>
        <ul className="navbar__mobile-links">
          {NAV_LINKS.map(({ label, href }) => (
            <li key={label}>
              <Link to={href} className="navbar__mobile-link">{label}</Link>
            </li>
          ))}
        </ul>
        <div className="navbar__mobile-lang">
          {['Telugu', 'Hindi', 'English'].map(l => (
            <button key={l} className={`lang-pill${lang === l ? ' lang-pill--active' : ''}`}
              onClick={() => setLang(l)}>{l}</button>
          ))}
        </div>
        <a href="/chat" className="navbar__wa navbar__wa--mobile">
          💬 Chat with Tilak
        </a>
      </div>
    </nav>
  );
};

// Standardized VTD sun-with-rays logo — matches splash screen and onboarding
const SunIcon = () => (
  <svg className="navbar__sun" viewBox="0 0 40 40" width="32" height="32" aria-hidden="true">
    <line x1="32"    y1="20"    x2="37"    y2="20"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="28.49" y1="28.49" x2="32.02" y2="32.02" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20"    y1="32"    x2="20"    y2="37"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="11.51" y1="28.49" x2="7.98"  y2="32.02" stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="8"     y1="20"    x2="3"     y2="20"    stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="11.51" y1="11.51" x2="7.98"  y2="7.98"  stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="20"    y1="8"     x2="20"    y2="3"     stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <line x1="28.49" y1="11.51" x2="32.02" y2="7.98"  stroke="#FFD700" strokeWidth="1.8" strokeLinecap="round"/>
    <circle cx="20" cy="20" r="9" fill="#D4AF37"/>
    <circle cx="20" cy="20" r="5" fill="#FFD700"/>
    <circle cx="20" cy="20" r="2" fill="#FFF8C0" opacity="0.7"/>
  </svg>
);

const WAIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default Navbar;
